<template>
    <div class="container-fluid pt-2">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Sales Closing</h5>
                    <div class="d-flex align-items-center">
                        <button class="btn btn-sm btn-primary" @click="isForm = !isForm; data.value = null"><i
                                class="fa fa-solid fa-plus"></i>
                            Create</button>
                        <button class="btn btn-sm btn-warning ms-1" @click="processRecalculate" :disabled="loading"><i
                                class="fa fa-solid fa-rotate-right"></i>
                            Re-Calculate</button>
                        <button type="button" class="btn btn-sm btn-info ms-1 me-1" @click="processDownloadXls"
                            :disabled="loading"><i class="fa-regular fa-file-excel"></i>
                            XLS</button>
                    </div>
                </div>
            </div>
            <div class="card-body py-1" id="body-salesorder">
                <div class="row">
                    <div class="col-md-3 mb-3">
                        <select v-model="params.divisi_id" class="form-select">
                            <option :value="''" disabled>Select Divisi</option>
                            <option v-for="option in divisiOptions" :key="option.ID" :value="option.ID">
                                {{ (option as any).name }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="card-body py-1">
                <DataTable :headers="headers" :items="salesClosing" :loading="loading" :total="total" :params="params"
                    @update:params="updateParams">
                    <template #item.closing_date="{ item }" style="white-space: nowrap;word-wrap: break-word;">{{
                        formatDateTime(item.closing_date) }}</template>
                    <template #item.customer="{ item }" style="white-space: nowrap;word-wrap: break-word;">{{
                        item.customer }}</template>
                    <template #item.total_so="{ item }" style="white-space: nowrap;word-wrap: break-word;">{{
                        totalSo(item.closing_details) }}</template>
                    <template #item.total_produk="{ item }" style="white-space: nowrap;word-wrap: break-word;">{{
                        totalProduk(item.closing_details) }}</template>
                    <template #item.total_omset="{ item }" style="white-space: nowrap;word-wrap: break-word;">{{
                        totalOmset(item.closing_details) }}</template>
                    <template #item.total_release="{ item }" style="white-space: nowrap;word-wrap: break-word;">{{
                        totalRelease(item.closing_details) }}</template>
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-warning" @click="isView = true; data = item">
                            <i class="fa fa-solid fa-eye"></i>
                        </button> &nbsp;
                        <button class="btn btn-sm btn-warning" @click="isForm = true; data = item">
                            <i class="fa fa-solid fa-pencil"></i>
                        </button> &nbsp;
                        <button class="btn btn-sm btn-danger" @click="deleteData(item.ID)">
                            <i class="fa fa-solid fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>
    <Form title="Create Sales Closing" v-if="isForm" :data="data" @on-closed="isForm = false; fetchSalesClosing()" />
    <View title="View Sales Closing" v-if="isView" :data="data" @on-closed="isView = false" />
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, defineAsyncComponent } from 'vue'
const Form = defineAsyncComponent(() => import("./Form.vue"))
const View = defineAsyncComponent(() => import("./View.vue"))
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { useSessionStore } from '@/store/session';
const session = useSessionStore()
import DataTable from '@/components/table/DatatableDateRange.vue'
import * as sales from '@/api/services/SalesRequest'
const isForm = ref(false)
const isView = ref(false)
const data = ref<any>(null)

const divisiOptions = ref<{ ID: any }[]>([])
const divisiTeamOptions = ref<{ ID: any, name: string }[]>([])
// {{ Define SalesOrder interface }}

const headers = [
    { text: 'Closing No', value: 'code' },
    { text: 'Closing Date', value: 'closing_date' },
    { text: 'Divisi', value: 'divisi_name' },
    { text: 'Note', value: 'notes' },
    { text: 'Total SO', value: 'total_so' },
    { text: 'Total Produk', value: 'total_produk' },
    { text: 'Total Omset', value: 'total_omset' },
    { text: 'Total Release', value: 'total_release' },
    { text: '#Act', value: 'actions' }
]

// {{ Apply types to refs and reactive objects }}
const salesClosing = ref<any[]>([])
const loading = ref(false)
const total = ref(0)
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: '',
    divisi_id: '',
    divisi_team_id: ''
})

const fetchSalesClosing = async () => {
    data.value = {}
    loading.value = true
    try {
        // Simulate API delay
        await sales.postRequest("/sales-closing/list", params).then((response) => {
            salesClosing.value = response.data;
            total.value = response.total
        });
    } catch (error) {
        toast.error('Failed to fetch sales closing')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchSalesClosing()
}

onMounted(() => {
    divisiOptions.value = session.divisionOptions()
    params.divisi_id = divisiOptions.value.find((item) => item.name === session.getUser().divisi)?.ID
})

function formatDateTime(dateString: string, part: string = "-", partTime: string = " "): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${dd}${part}${mm}${part}${yyyy}${partTime}${hh}:${min}`;
}

function totalSo(items: any[]) {
    return separator(items.reduce((total, item) => total + 1, 0));
}

function totalProduk(items: any[]) {
    return separator(items.reduce((total, item) => total + item.order.qty_total, 0));
}
function totalOmset(items: any[]) {
    return formatCurrency(items.reduce((total, item) => total + item.order.sub_total, 0));
}
function totalRelease(items: any[]) {
    return formatCurrency(items.reduce((total, item) => total + item.price, 0));
}

function separator(value: number) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatCurrency(value: number) {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
    }).format(value);
}

const processDownloadXls = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const prms = { ...params }
        prms.size = 10000
        try {
            const response = await sales.getDownload("/sales-closing/xlsx", prms);
            // Create blob from response data
            const blob = new Blob([response], { type: 'application/vnd.ms-excel' });
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sales-closing.xlsx');
            // Trigger download
            document.body.appendChild(link);
            link.click();
            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (error) {
            toast.error('Failed to download file. Please try again.');
        }

    } catch (error) {
        toast.error('Failed to fetch delivery')
    }
    loading.value = false
}

const processRecalculate = async () => {
    if (!confirm('Re-calculate closed date untuk order yang masih kosong?')) return
    loading.value = true
    try {
        const resp = await sales.postRequest("/sales-closing/recalculate", {})
        const rows = (resp as any)?.data?.rows_affected ?? 0
        toast.success(`Re-calculate sukses. Rows: ${rows}`)
        fetchSalesClosing()
    } catch (error: any) {
        toast.error(error?.message || 'Failed to re-calculate')
    } finally {
        loading.value = false
    }
}

const deleteData = async (id: number) => {
    if (!confirm('Are you sure you want to delete this Sales Closing?')) return

    loading.value = true
    try {
        await sales.postRequest("/sales-closing/delete", { ID: id }).then((resp) => {
            if (resp.status === 200) {
                toast.success('Sales Closing deleted successfully');
                fetchSalesClosing();
            } else {
                toast.error('Failed to delete Sales Closing');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete Sales Closing');
    } finally {
        loading.value = false
    }
}
</script>
